import { useState } from "react";
import { Loader2, MessageSquare, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

function FeedbackForm({ type }) {
  const { t } = useI18n();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (message.trim().length < 3) return;
    setSending(true);
    try {
      await api.post("/feedback", { type, message: message.trim() });
      toast.success(t("settings.feedback_sent"));
      setMessage("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-app-border p-5">
      <label className="text-[10px] uppercase tracking-widest text-olive font-bold">
        {t(type === "suggestion" ? "settings.suggestion" : "settings.complaint")}
      </label>
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t(type === "suggestion" ? "settings.suggestion_ph" : "settings.complaint_ph")}
        rows={5}
        className="mt-1"
        data-testid={`${type}-textarea`}
      />
      <Button
        onClick={send}
        disabled={sending || message.trim().length < 3}
        size="sm"
        className="mt-2 bg-olive hover:bg-olive/90"
        data-testid={`${type}-send-button`}
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t("settings.send")}
      </Button>
    </div>
  );
}

export default function FeedbackPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="feedback-page">
      <button
        type="button"
        onClick={() => navigate("/settings")}
        className="flex items-center gap-1.5 text-xs font-semibold text-espresso/60 hover:text-espresso transition-colors"
        data-testid="feedback-back-button"
      >
        <ArrowLeft className="w-3.5 h-3.5 rtl:rotate-180" />
        {t("account.title")}
      </button>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
        <MessageSquare className="w-7 h-7 text-olive" />
        {t("settings.feedback")}
      </h1>
      <FeedbackForm type="suggestion" />
      <FeedbackForm type="complaint" />
    </div>
  );
}
